import { Spacing } from '@persona-id/design-tokens';
import { ChangeEvent, FC, useContext, useMemo, useState } from 'react';
import { VisuallyHidden } from 'react-aria';
import { WrappedFieldProps } from 'redux-form';
import Markdown from 'shared/components/Markdown';
import MaterialInput from 'shared/components/MaterialInput';
import styled, { DefaultTheme, ThemeContext, withTheme } from 'styled-components';

import FormError from 'inquiry/components/FormError';
import MaterialSelect from 'inquiry/components/MaterialSelect';
import Select from 'inquiry/components/Select';
import ThemedFormInputBase from 'inquiry/components/ThemedFormInput/ThemedFormInputBase';
import { containerSSTStyles, labelSSTStyles, materialContainerStyles } from 'inquiry/mixins/form';
import FormControlProps from 'lib/interfaces/FormControlProps';
import { InputStyles } from 'lib/interfaces/input-styles';
import { InputDateComponent, InputSelectComponent } from 'lib/interfaces/ui-components';

// styles borrowed from VisuallyHidden in 'react-aria'
const VisuallyHiddenSpan = styled('span')`
  border: 0;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
  white-space: nowrap;
`;

const OutlinedContainer = styled('div')<{
  styles?: InputDateComponent['styles'];
}>`
  ${({ styles }) => containerSSTStyles(styles)}
  display: flex;
  flex-wrap: wrap;
  margin-left: -${Spacing[2]};
  margin-right: -${Spacing[2]};
`;

const MaterialContainer = styled('div')`
  ${materialContainerStyles}
  display: flex;
  flex-wrap: wrap;
  margin-left: -${Spacing[2]};
  margin-right: -${Spacing[2]};
`;

const OutlinedLabelContainer = styled('div')<{ styles: InputDateComponent['styles'] }>`
  ${({ styles }) => labelSSTStyles(styles)}
`;

const ErrorContainer = styled('div')`
  /* To account for negative margin in parent */
  padding-left: ${Spacing[2]};
`;

const Month = styled('div')`
  flex: 0 0 40%;
  max-width: 40%;
  padding-left: ${Spacing[2]};
  padding-right: ${Spacing[2]};
  text-transform: capitalize;
`;

const DayOrYear = styled('div')`
  flex: 0 0 30%;
  max-width: 30%;
  padding-left: ${Spacing[2]};
  padding-right: ${Spacing[2]};
`;

interface ThemedFormDateInputProps extends FormControlProps {
  disabled?: boolean;
  theme: DefaultTheme;
  textMonths: string[];
  label: string;
  placeholderMonth: string;
  placeholderDay: string;
  placeholderYear: string;
  labelMonth: string;
  labelDay: string;
  labelYear: string;
  submitFailed: boolean;
  styles?: InputDateComponent['styles'];
}

interface DeserializedDate {
  year: string;
  month: string;
  day: string;
}

const ThemedFormDateInput: FC<ThemedFormDateInputProps & WrappedFieldProps> = ({
  disabled,
  input,
  label,
  meta: { error, submitFailed },
  textMonths,
  placeholderDay,
  placeholderMonth,
  placeholderYear,
  labelMonth,
  labelDay,
  labelYear,
  styles,
}: ThemedFormDateInputProps & WrappedFieldProps) => {
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');

  const theme = useContext(ThemeContext);

  /**
   * When date is incomplete, it must be stored in state since value is full date string.
   */
  const deserializedValue = useMemo((): DeserializedDate => {
    if (!/\d{4}-\d{2}-\d{2}/.test(input.value)) {
      return {
        year: year,
        month: month,
        day: day,
      };
    } else {
      const date = new Date(input.value);

      return {
        year: String(date.getUTCFullYear()),
        month: String(date.getUTCMonth()),
        day: String(date.getUTCDate()),
      };
    }
  }, [input.value, year, month, day]);

  const handleChangeDay = (event: ChangeEvent<HTMLInputElement>) => {
    propagateDateChange({
      year: deserializedValue.year,
      month: deserializedValue.month,
      day: event.target.value,
    });
  };

  const handleChangeMonth = (event: ChangeEvent<HTMLSelectElement>) => {
    propagateDateChange({
      year: deserializedValue.year,
      month: event.target.value,
      day: deserializedValue.day,
    });
  };

  const handleChangeYear = (event: ChangeEvent<HTMLInputElement>) => {
    propagateDateChange({
      year: event.target.value,
      month: deserializedValue.month,
      day: deserializedValue.day,
    });
  };

  const propagateDateChange = (newValue: DeserializedDate) => {
    setDay(newValue.day);
    setMonth(newValue.month);
    setYear(newValue.year);

    const newYear = parseInt(newValue.year, 10);
    const newMonth = parseInt(newValue.month, 10);
    const newDay = parseInt(newValue.day, 10);

    const dateValue = new Date(Date.UTC(newYear, newMonth, newDay));
    // Make sure it's valid by seeing whether inference changed any field
    if (
      !isNaN(dateValue.getTime()) &&
      dateValue.getUTCFullYear() === newYear &&
      dateValue.getUTCMonth() === newMonth &&
      dateValue.getUTCDate() === newDay
    ) {
      const parsedDate = dateValue.toISOString().split('T')[0];
      input.onChange(parsedDate);
    } else {
      // Must be '', null or undefined doesn't update the form value
      input.onChange('');
    }
  };

  const renderOutlined = () => {
    const hasError = !!(error && submitFailed);

    return (
      <OutlinedLabelContainer styles={styles}>
        {label && (
          <Markdown<'label'>
            wrapper='label'
            wrapperProps={{
              // remove gap between this and sublabels, if any sublabel exists
              style: { marginBottom: labelMonth || labelYear || labelDay ? '0' : '8px' },
            }}
          >
            {label}
          </Markdown>
        )}
        <OutlinedContainer styles={styles}>
          <Month>
            {labelMonth && (
              <label htmlFor={input.name + '-month'}>
                <VisuallyHiddenSpan>
                  {label && <Markdown>{`${label}, `}</Markdown>}
                </VisuallyHiddenSpan>
                <Markdown
                  wrapperProps={{ role: 'presentation', style: { marginBottom: Spacing[2] } }}
                >
                  {labelMonth}
                </Markdown>
              </label>
            )}
            <Select
              id={input.name + '-month'}
              name={input.name + '-month'}
              disabled={disabled}
              hasError={hasError}
              onChange={handleChangeMonth}
              aria-invalid={hasError}
              aria-errormessage={`${input.name}--error`}
              aria-describedby={`${input.name}--error`}
              value={deserializedValue.month}
              // FIXME: this is NOT the correct type. The server doesn't send complete types.
              // Need to fix server styles to use nested component styles.
              styles={styles as InputSelectComponent['styles']}
            >
              <option value='' disabled>
                {placeholderMonth}
              </option>
              {textMonths.map((monthName, idx) => (
                <option key={idx} value={idx}>
                  {monthName}
                </option>
              ))}
            </Select>
          </Month>
          <DayOrYear>
            {labelDay && (
              <label htmlFor={input.name + '-day'}>
                <VisuallyHiddenSpan>
                  {label && <Markdown>{`${label}, `}</Markdown>}
                </VisuallyHiddenSpan>
                <Markdown
                  wrapperProps={{ role: 'presentation', style: { marginBottom: Spacing[2] } }}
                >
                  {labelDay}
                </Markdown>
              </label>
            )}
            <ThemedFormInputBase
              id={input.name + '-day'}
              name={input.name + '-day'}
              disabled={disabled}
              hasError={hasError}
              min={1}
              max={31}
              onChange={handleChangeDay}
              placeholder={placeholderDay}
              aria-invalid={hasError}
              aria-errormessage={`${input.name}--error`}
              aria-describedby={`${input.name}--error`}
              type='number'
              // Default to empty string so it starts as a controlled input
              value={deserializedValue.day}
              styles={styles}
            />
          </DayOrYear>
          <DayOrYear>
            {labelYear && (
              <label htmlFor={input.name + '-year'}>
                <VisuallyHiddenSpan>
                  {label && <Markdown>{`${label}, `}</Markdown>}
                </VisuallyHiddenSpan>
                <Markdown
                  wrapperProps={{ role: 'presentation', style: { marginBottom: Spacing[2] } }}
                >
                  {labelYear}
                </Markdown>
              </label>
            )}
            <ThemedFormInputBase
              id={input.name + '-year'}
              name={input.name + '-year'}
              disabled={disabled}
              hasError={hasError}
              min={1900}
              onChange={handleChangeYear}
              placeholder={placeholderYear}
              aria-invalid={hasError}
              aria-errormessage={`${input.name}--error`}
              aria-describedby={`${input.name}--error`}
              type='number'
              // Default to empty string so it starts as a controlled input
              value={deserializedValue.year}
              styles={styles}
            />
          </DayOrYear>
          <ErrorContainer>
            <FormError
              id={`${input.name}--error`}
              hasError={hasError}
              errorMessage={error}
              styles={styles}
            />
          </ErrorContainer>
        </OutlinedContainer>
      </OutlinedLabelContainer>
    );
  };

  const renderMaterial = () => {
    const hasError = !!(error && submitFailed);

    return (
      <MaterialContainer>
        <Month>
          <VisuallyHidden>
            <label htmlFor={input.name + '-month'}>{`${label}, ${placeholderMonth}`}</label>
          </VisuallyHidden>
          <MaterialSelect
            id={input.name + '-month'}
            name={input.name + '-month'}
            disabled={disabled}
            hasError={hasError}
            onChange={handleChangeMonth}
            aria-invalid={hasError}
            aria-errormessage={`${input.name}--error`}
            aria-describedby={`${input.name}--error`}
            value={deserializedValue.month}
            labelText={label}
            // FIXME: this is NOT the correct type. The server doesn't send complete types.
            // Need to fix server styles to use nested component styles.
            styles={styles as InputSelectComponent['styles']}
          >
            <option value='' disabled>
              {placeholderMonth}
            </option>
            {textMonths.map((monthName, idx) => (
              <option key={idx} value={idx}>
                {monthName}
              </option>
            ))}
          </MaterialSelect>
        </Month>
        <DayOrYear>
          <VisuallyHidden>
            <label htmlFor={input.name + '-day'}>{`${label}, ${placeholderDay}`}</label>
          </VisuallyHidden>
          <MaterialInput
            id={input.name + '-day'}
            name={input.name + '-day'}
            disabled={disabled}
            hasError={hasError}
            min={1}
            max={31}
            onChange={handleChangeDay}
            aria-invalid={hasError}
            aria-errormessage={`${input.name}--error`}
            aria-describedby={`${input.name}--error`}
            type='number'
            // Default to empty string so it starts as a controlled input
            value={deserializedValue.day}
            labelText={placeholderDay}
            styles={styles}
          />
        </DayOrYear>
        <DayOrYear>
          <VisuallyHidden>
            <label htmlFor={input.name + '-year'}>{`${label}, ${placeholderYear}`}</label>
          </VisuallyHidden>
          <MaterialInput
            id={input.name + '-year'}
            name={input.name + '-year'}
            disabled={disabled}
            hasError={hasError}
            min={1900}
            max={9999}
            onChange={handleChangeYear}
            labelText={placeholderYear}
            aria-invalid={hasError}
            aria-errormessage={`${input.name}--error`}
            aria-describedby={`${input.name}--error`}
            type='number'
            // Default to empty string so it starts as a controlled input
            value={deserializedValue.year}
            styles={styles}
          />
        </DayOrYear>
        <ErrorContainer>
          <FormError
            id={`${input.name}--error`}
            hasError={hasError}
            errorMessage={error}
            styles={styles}
          />
        </ErrorContainer>
      </MaterialContainer>
    );
  };

  switch (theme.inputStyle) {
    case InputStyles.Material:
      return renderMaterial();
    case InputStyles.Outlined:
    default:
      return renderOutlined();
  }
};

export default withTheme(ThemedFormDateInput);
